

/* ============================================================
   Video Slider — slider carousel + video play behavior
   Vanilla JS, Dawn-style custom elements, no dependencies.
   ============================================================ */

class VideoSliderCarousel extends HTMLElement {
  constructor() {
    super();
    this.track       = null;
    this.slides      = [];
    this.currentIndex = 0;
    this.slidesPerView = 1;
    this.slideWidth  = 0;
    this.gap         = 0;
    this._clonesBefore = 0;
    this._clonesAfter  = 0;
    this._isTransitioning = false;

    this._onResize       = this._onResize.bind(this);
    this._onPrev         = this._onPrev.bind(this);
    this._onNext         = this._onNext.bind(this);
    this._onTouchStart   = this._onTouchStart.bind(this);
    this._onTouchMove    = this._onTouchMove.bind(this);
    this._onTouchEnd     = this._onTouchEnd.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);
  }

  connectedCallback() {
    this.track  = this.querySelector('[data-slider-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slider-slide]'));

    this.prevBtns = Array.from(this.querySelectorAll('[data-direction="prev"]'));
    this.nextBtns = Array.from(this.querySelectorAll('[data-direction="next"]'));
    this.prevBtns.forEach(btn => btn.addEventListener('click', this._onPrev));
    this.nextBtns.forEach(btn => btn.addEventListener('click', this._onNext));

    this.track.addEventListener('touchstart',    this._onTouchStart, { passive: true });
    this.track.addEventListener('touchmove',     this._onTouchMove,  { passive: true });
    this.track.addEventListener('touchend',      this._onTouchEnd);
    this.track.addEventListener('transitionend', this._onTransitionEnd);

    window.addEventListener('resize', this._onResize);

    this._bindVideoPlayButtons();
    this._buildClones();
    this._goTo(0, false);
    this._updateArrows();
  }

  disconnectedCallback() {
    this.prevBtns?.forEach(btn => btn.removeEventListener('click', this._onPrev));
    this.nextBtns?.forEach(btn => btn.removeEventListener('click', this._onNext));
    this.track?.removeEventListener('touchstart',    this._onTouchStart);
    this.track?.removeEventListener('touchmove',     this._onTouchMove);
    this.track?.removeEventListener('touchend',      this._onTouchEnd);
    this.track?.removeEventListener('transitionend', this._onTransitionEnd);
    window.removeEventListener('resize', this._onResize);
  }

  /* ── Seamless flag ────────────────────────────────────── */

  _isSeamless() {
    return this.dataset.seamlessMobile === 'true';
  }

  /* ── Clone management ─────────────────────────────────── */

  _removeClones() {
    this.track.querySelectorAll('[data-clone]').forEach(el => el.remove());
    this._clonesBefore = 0;
    this._clonesAfter  = 0;
  }

  _buildClones() {
    this._removeClones();
    if (!this._isSeamless() || this.slides.length === 0) return;

    const cloneCount = Math.min(this.slides.length, Math.ceil(this._getResponsiveSlidesPerView()) + 1);

    // Append clones of FIRST slides after the last real slide
    for (let i = 0; i < cloneCount; i++) {
      const clone = this.slides[i % this.slides.length].cloneNode(true);
      clone.setAttribute('data-clone', 'after');
      clone.setAttribute('aria-hidden', 'true');
      this.track.appendChild(clone);
    }

    // Prepend clones of LAST slides before the first real slide
    for (let i = cloneCount - 1; i >= 0; i--) {
      const clone = this.slides[this.slides.length - 1 - (i % this.slides.length)].cloneNode(true);
      clone.setAttribute('data-clone', 'before');
      clone.setAttribute('aria-hidden', 'true');
      this.track.insertBefore(clone, this.track.firstChild);
    }

    this._clonesBefore = cloneCount;
    this._clonesAfter  = cloneCount;
    // Expose for dots script
    this.dataset.clonesBefore = cloneCount;
  }

  /* ── Responsive helpers ───────────────────────────────── */

  _getResponsiveSlidesPerView() {
    const w = window.innerWidth;
    if (w <= 749) return parseInt(this.dataset.slidesMobile, 10) || 2;
    if (w <= 989) return parseInt(this.dataset.slidesTablet, 10) || 3;
    return parseInt(this.dataset.slidesDesktop, 10) || 5;
  }

  _measure() {
    this.slidesPerView = this._getResponsiveSlidesPerView();
    if (this.slides.length === 0) return;

    const allCells  = Array.from(this.track.children);
    const firstReal = allCells[this._clonesBefore] || allCells[0];
    this.slideWidth = firstReal.getBoundingClientRect().width;

    const trackStyle = window.getComputedStyle(this.track);
    this.gap = parseFloat(trackStyle.columnGap || trackStyle.gap || '0') || 0;
  }

  _maxIndex() {
    return Math.max(0, this.slides.length - this.slidesPerView);
  }

  /* ── Core position setter ─────────────────────────────── */

  _goTo(realIndex, animate = true) {
    this._measure();

    // DOM position accounts for prepended clones
    const domIndex = this._clonesBefore + realIndex;
    const offset   = -1 * domIndex * (this.slideWidth + this.gap);

    this.track.style.transition = animate
      ? 'transform 0.45s cubic-bezier(0.22, 0.61, 0.36, 1)'
      : 'none';

    if (animate) this._isTransitioning = true;

    this.track.style.transform = `translate3d(${offset}px, 0, 0)`;
    this.currentIndex = realIndex;
  }

  /* ── Silent reset after sliding into clone zone ───────── */

  _onTransitionEnd() {
    if (!this._isSeamless()) { this._isTransitioning = false; return; }

    const total = this.slides.length;
    let resetIndex = this.currentIndex;
    let needsReset = false;

    if (this.currentIndex < 0) {
      // Slid left past the first real slide — jump to end
      resetIndex = total + this.currentIndex;
      needsReset = true;
    } else if (this.currentIndex >= total) {
      // Slid right past the last real slide — jump to start
      resetIndex = this.currentIndex - total;
      needsReset = true;
    }

    if (needsReset) {
      this._goTo(resetIndex, false);
    }

    this._isTransitioning = false;
  }

  /* ── Resize ───────────────────────────────────────────── */

  _update(animate = false) {
    this._buildClones();
    this._goTo(this.currentIndex, animate);
    this._updateArrows();
  }

  _onResize() {
    clearTimeout(this._resizeT);
    this._resizeT = setTimeout(() => this._update(false), 120);
  }

  /* ── Arrow state ──────────────────────────────────────── */

  _updateArrows() {
    const seamless   = this._isSeamless();
    const allVisible = this.slides.length <= this.slidesPerView;

    this.prevBtns?.forEach(btn => {
      btn.disabled = seamless ? false : this.currentIndex <= 0;
      btn.style.visibility = allVisible ? 'hidden' : 'visible';
    });
    this.nextBtns?.forEach(btn => {
      btn.disabled = seamless ? false : this.currentIndex >= this._maxIndex();
      btn.style.visibility = allVisible ? 'hidden' : 'visible';
    });

    this.classList.toggle('video-slider__carousel-wrapper--no-nav', allVisible);
  }

  /* ── Arrow clicks ─────────────────────────────────────── */

  _onPrev() {
    if (this._isTransitioning) return;
    this._goTo(this.currentIndex - 1, true);
    this._updateArrows();
  }

  _onNext() {
    if (this._isTransitioning) return;
    this._goTo(this.currentIndex + 1, true);
    this._updateArrows();
  }

  /* ── Touch / swipe ────────────────────────────────────── */

  _onTouchStart(e) {
    this._touchStartX = e.touches[0].clientX;
    this._touchStartY = e.touches[0].clientY;
    this._touchMoved  = false;
    this._touchDx     = 0;
  }

  _onTouchMove(e) {
    if (this._touchStartX == null) return;
    const dx = e.touches[0].clientX - this._touchStartX;
    const dy = e.touches[0].clientY - this._touchStartY;
    if (Math.abs(dx) > Math.abs(dy)) this._touchMoved = true;
    this._touchDx = dx;
  }

  _onTouchEnd() {
    if (this._touchMoved && Math.abs(this._touchDx || 0) > 40) {
      if (this._touchDx < 0) this._onNext();
      else this._onPrev();
    }
    this._touchStartX = null;
    this._touchStartY = null;
    this._touchDx     = 0;
  }

  /* ── Video play buttons ───────────────────────────────── */

  _bindVideoPlayButtons() {
    this.querySelectorAll('[data-play-video]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const mediaEl     = btn.closest('video-slider-item');
        if (!mediaEl) return;
        const wrapper     = mediaEl.querySelector('[data-video-wrapper]');
        const nativeVideo = mediaEl.querySelector('[data-video]');
        const iframe      = mediaEl.querySelector('[data-video-iframe]');
        if (!wrapper) return;

        wrapper.hidden = false;

        if (nativeVideo) {
          nativeVideo.play().catch(() => {});
        } else if (iframe && !iframe.src) {
          iframe.src = iframe.dataset.videoSrc;
        }
      });
    });
  }
}

/* ── No-op custom element ─────────────────────────────── */
class VideoSliderItem extends HTMLElement {}

if (!customElements.get('video-slider-carousel')) {
  customElements.define('video-slider-carousel', VideoSliderCarousel);
}
if (!customElements.get('video-slider-item')) {
  customElements.define('video-slider-item', VideoSliderItem);
}

/* ── Theme editor support ─────────────────────────────── */
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const carousel = event.target.querySelector('video-slider-carousel');
    if (carousel && typeof carousel._update === 'function') {
      setTimeout(() => carousel._update(false), 50);
    }
  });
}

/* ── Pagination dots ──────────────────────────────────── */
(function () {
  function initDots(carousel) {
    const track         = carousel.querySelector('[data-slider-track]');
    const dotsContainer = carousel.querySelector('[data-slider-dots]');
    // Only count real slides, not clones
    const slides        = Array.from(carousel.querySelectorAll('[data-slider-slide]:not([data-clone])'));
    const prevBtn       = carousel.querySelector('[data-direction="prev"]');
    const nextBtn       = carousel.querySelector('[data-direction="next"]');

    if (!track || !dotsContainer || !slides.length) return;

    function getSlidesPerView() {
      const w = window.innerWidth;
      if (w <= 749) return parseInt(carousel.dataset.slidesMobile, 10) || 2;
      if (w <= 989) return parseInt(carousel.dataset.slidesTablet, 10) || 3;
      return parseInt(carousel.dataset.slidesDesktop, 10) || 5;
    }

    function getCurrentIndex() {
      const slideWidth   = slides[0].getBoundingClientRect().width;
      const gap          = parseFloat(getComputedStyle(track).gap) || 0;
      const transform    = getComputedStyle(track).transform;
      let translateX     = 0;
      if (transform && transform !== 'none') {
        const match = transform.match(/matrix.*\((.+)\)/);
        if (match) {
          translateX = Math.abs(parseFloat(match[1].split(', ')[4] || 0));
        }
      }
      const clonesBefore = parseInt(carousel.dataset.clonesBefore || 0, 10);
      const rawIndex     = Math.round(translateX / (slideWidth + gap));
      return Math.max(0, rawIndex - clonesBefore);
    }

    function buildDots() {
      const perView   = getSlidesPerView();
      const pageCount = Math.max(1, Math.ceil(slides.length / perView));
      dotsContainer.innerHTML = '';

      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement('button');
        dot.type      = 'button';
        dot.className = 'video-slider__dot';
        dot.setAttribute('aria-label', 'Go to slide group ' + (i + 1));
        dot.addEventListener('click', function () {
          if (typeof carousel._goTo === 'function') {
            carousel._goTo(i * perView, true);
          }
          setTimeout(updateActiveDot, 50);
        });
        dotsContainer.appendChild(dot);
      }
      updateActiveDot();
    }

    function updateActiveDot() {
      const perView    = getSlidesPerView();
      const currentIdx = getCurrentIndex();
      const activePage = Math.round(currentIdx / perView);
      dotsContainer.querySelectorAll('.video-slider__dot').forEach(function (dot, i) {
        dot.classList.toggle('video-slider__dot--active', i === activePage);
      });
    }

    if (prevBtn) prevBtn.addEventListener('click', () => setTimeout(updateActiveDot, 100));
    if (nextBtn) nextBtn.addEventListener('click', () => setTimeout(updateActiveDot, 100));

    new MutationObserver(() => setTimeout(updateActiveDot, 50))
      .observe(track, { attributes: true, attributeFilter: ['style'] });

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(buildDots, 200);
    });

    buildDots();
  }

  function initAll() {
    document.querySelectorAll('video-slider-carousel').forEach(initDots);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();